import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { Box, Button, Card, Group, Text } from '@grovus/design-system'
import { removeMember } from '../data/members'
import { MEMBERS_COPY, SHARE_COPY, type Dict, type Lang } from '../i18n'
import { fmt } from '../lib/format'
import { PanelHeading } from './PanelHeading'
import type { PanelProps } from './useDisclosurePanel'

/** The active company's section of the menu: Members and Leave client. */
export interface UserMenuCompany {
  id: string
  name: string
  /** The signed-in user, whose own membership Leave removes. */
  userId: string
  /** The user is the company's only Owner, by the `ready` member list. Unknown counts as not. */
  leaveLocked: boolean
  onOpenMembers: () => void
  /** Owner or Admin of the company: "Share read-only" is enabled (GRO-83). */
  canShare: boolean
  onShare: () => void
  /** After the membership is gone: the boundary takes the "gone" path without the banner. */
  onLeft: (companyId: string) => Promise<void>
}

/** What the menu knows about the signed-in account (GRO-106 design, "Header, switcher and user menu"). */
export interface UserMenuAccount {
  email: string
  invitationCount: number
  onOpenInvitations: () => void
  company?: UserMenuCompany
}

interface Props {
  t: Dict
  lang?: Lang
  account?: UserMenuAccount
  /** App's `SessionSettings`: the settings section and sign-out. */
  settings: ReactNode
  panelProps: PanelProps
}

const SECTION: CSSProperties = { borderTop: '1px solid var(--mantine-color-default-border)' }

type LeaveState = 'idle' | 'confirm' | 'busy' | 'lastOwner' | 'failed'

/**
 * The user menu behind the header's avatar: the email, Invitations with its count, the
 * active company's Members and Leave client, Access with "Share read-only" (GRO-83), then the settings and sign-out (GRO-106,
 * boards 8 and `9-leave`). Leave confirms inline in the open menu. The `409 last_owner`
 * answer is the gate whenever the Owner count is unknown, so the reason can also arrive
 * after the confirmation.
 */
export function UserMenu({ t, lang = 'en', account, settings, panelProps }: Props) {
  const copy = MEMBERS_COPY[lang]
  const shareCopy = SHARE_COPY[lang]
  const company = account?.company
  const [leave, setLeave] = useState<{ companyId: string | null; state: LeaveState }>({ companyId: null, state: 'idle' })
  const alive = useRef(true)
  useEffect(() => {
    alive.current = true
    return () => { alive.current = false }
  }, [])
  // A confirmation belongs to the company it was opened for.
  const leaveState: LeaveState = company !== undefined && leave.companyId === company.id ? leave.state : 'idle'
  const leaveDisabled = company?.leaveLocked === true || leaveState === 'busy'

  async function confirmLeave(target: UserMenuCompany) {
    setLeave({ companyId: target.id, state: 'busy' })
    const outcome = await removeMember(target.id, target.userId)
    if (!alive.current) return
    if (outcome.status === 'ok' || outcome.status === 'notFound') {
      await target.onLeft(target.id)
      return
    }
    if (outcome.status === 'stale' || outcome.status === 'expired') return
    const state: LeaveState = outcome.status === 'refused' && outcome.code === 'last_owner' ? 'lastOwner' : 'failed'
    setLeave({ companyId: target.id, state })
  }

  return (
    <Card
      role="group"
      aria-label={t.userMenu}
      className="header-panel user-menu"
      style={{ right: 0, '--panel-width': '300px' } as CSSProperties}
      {...panelProps}
    >
      {account !== undefined && (
        <>
          <Box>
            <Text fw={600} fz="sm" style={{ overflowWrap: 'anywhere' }} data-testid="user-menu-email">{account.email}</Text>
            <Text fz="sm" className="grv-muted">{copy.google}</Text>
          </Box>

          <Box pt="sm" mt="sm" style={SECTION}>
            <Button
              variant="subtle"
              fullWidth
              justify="space-between"
              px={4}
              onClick={account.onOpenInvitations}
              rightSection={account.invitationCount > 0 ? (
                <span className="grv-num user-menu-badge" data-testid="invitations-badge">{account.invitationCount}</span>
              ) : undefined}
            >
              {copy.invitations}
            </Button>
          </Box>

          {company !== undefined && (
            <Box pt="sm" mt="sm" style={SECTION} data-testid="user-menu-company">
              <PanelHeading>{company.name}</PanelHeading>
              <Button variant="subtle" fullWidth justify="flex-start" px={4} onClick={company.onOpenMembers}>
                {copy.members}
              </Button>
              <Box>
                <Button
                  variant="subtle"
                  size="sm"
                  px={4}
                  c={leaveDisabled ? undefined : 'bad.6'}
                  disabled={leaveDisabled}
                  onClick={() => setLeave({ companyId: company.id, state: 'confirm' })}
                >
                  {copy.leave}
                </Button>
              </Box>
              {(company.leaveLocked || leaveState === 'lastOwner') && (
                <Text fz="sm" px={4} pt={2} className="grv-muted" data-testid="leave-why">{copy.leaveWhy}</Text>
              )}
              {!company.leaveLocked && (leaveState === 'confirm' || leaveState === 'busy' || leaveState === 'failed') && (
                <Box px={4} pt={4} data-testid="leave-confirm">
                  <Text fz="xs">{fmt(copy.confirmLeave, { client: company.name })}</Text>
                  {leaveState === 'failed' && <Text fz="xs" pt={4} role="alert">{copy.failed}</Text>}
                  <Group gap="xs" pt="xs">
                    <Button
                      variant="default"
                      size="compact-sm"
                      c="bad.6"
                      loading={leaveState === 'busy'}
                      onClick={() => { void confirmLeave(company) }}
                    >
                      {copy.leaveOk}
                    </Button>
                    <Button
                      variant="default"
                      size="compact-sm"
                      disabled={leaveState === 'busy'}
                      onClick={() => setLeave({ companyId: null, state: 'idle' })}
                    >
                      {copy.cancel}
                    </Button>
                  </Group>
                </Box>
              )}
            </Box>
          )}

          {company !== undefined && (
            <Box pt="sm" mt="sm" style={SECTION} data-testid="user-menu-access">
              <PanelHeading>{shareCopy.access}</PanelHeading>
              <Button
                variant="subtle"
                fullWidth
                justify="flex-start"
                px={4}
                disabled={!company.canShare}
                aria-describedby={company.canShare ? undefined : 'share-why'}
                onClick={company.onShare}
              >
                {shareCopy.share}
              </Button>
              {!company.canShare && (
                <Text id="share-why" fz="sm" px={4} pt={2} className="grv-muted">{shareCopy.shareWhy}</Text>
              )}
            </Box>
          )}
        </>
      )}

      {settings}
    </Card>
  )
}
